// packages/shared/src/i18n/init.ts
//
// i18next bootstrap shared by FE portals (admin / merchant / vendor) and the
// BullMQ email worker.:
//   - Namespaces: `common`, `errors` (this package owns both); per-app feature
//     namespaces (`auth`, `catalog`, ...) lazy-load on top.
//   - Locales: `vi` (default), `en`, `fr`.
//   - Fallback chain: `vi → en`, `fr → en`. The `fr` fallback exists because
//     French translations may lag behind EN/VI for new strings.
//   - Key naming: camelCase within namespaces, dotted nesting up to 2 levels.
//
// FIX-13 boundary: NO Fastify imports. The function is intentionally thin
// (`createI18n(opts)`) so apps can compose `react-i18next` (FE) or feed the
// instance into BullMQ workers without forcing a particular plugin set.
import i18nextDefault from 'i18next';
import enCommon from './locales/en/common.json' with { type: 'json' };
import enCookie from './locales/en/cookie.json' with { type: 'json' };
import enErrors from './locales/en/errors.json' with { type: 'json' };
import viCommon from './locales/vi/common.json' with { type: 'json' };
import viCookie from './locales/vi/cookie.json' with { type: 'json' };
import viErrors from './locales/vi/errors.json' with { type: 'json' };
import frCommon from './locales/fr/common.json' with { type: 'json' };
import frCookie from './locales/fr/cookie.json' with { type: 'json' };
import frErrors from './locales/fr/errors.json' with { type: 'json' };
// ---------------------------------------------------------------------------
// Resource Bundle (typed at build time)
// ---------------------------------------------------------------------------
/**
 * Pre-bundled locale resources shipped with this package. Apps can read them
 * directly when composing their own i18next instance instead of calling
 * `createI18n` (e.g. when using `react-i18next` with custom backends).
 */
export const sharedResources = {
    vi: { common: viCommon, errors: viErrors, cookie: viCookie },
    en: { common: enCommon, errors: enErrors, cookie: enCookie },
    fr: { common: frCommon, errors: frErrors, cookie: frCookie },
};
/** Fallback chain: vi→en, fr→en, default→en. */
export const fallbackChain = {
    vi: ['en'],
    fr: ['en'],
    default: ['en'],
};
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Create and initialize an i18next instance preloaded with the shared
 * common/errors namespaces. Returns the same instance on subsequent calls
 * so multi-tenant FE bundles share state.
 *
 * Apps that need their own isolated instance (e.g. SSR with per-request
 * locale) should call `i18next.createInstance()` directly and feed
 * `sharedResources` into `init()`.
 *
 * @example
 *   const i18n = await createI18n({ lng: 'vi' });
 *   i18n.t('common:save');         // "Lưu"
 *   i18n.t('errors:AUTH_TOKEN_EXPIRED');
 */
export async function createI18n(options = {}) {
    const { lng = 'vi', defaultNS = 'common', ns = ['common', 'errors'], resources, debug = false, } = options;
    // Merge caller-provided resources on top of the shipped bundles. Caller
    // resources WIN per i18next merge semantics (later keys override earlier).
    const mergedResources = {
        ...sharedResources,
        ...(resources ?? {}),
    };
    if (!i18nextDefault.isInitialized) {
        await i18nextDefault.init({
            lng,
            defaultNS,
            ns: Array.from(new Set([...ns, 'common', 'errors'])),
            fallbackLng: { ...fallbackChain },
            resources: mergedResources,
            interpolation: {
                // FE bundlers escape on render via React; re-escaping in i18next
                // would double-encode entities (`&amp;amp;`). Disable it.
                escapeValue: false,
            },
            // Keys missing in the active locale fall through the fallback chain.
            returnEmptyString: false,
            // Don't crash on missing keys — show the key itself so QA can spot gaps.
            returnNull: false,
            saveMissing: false,
            debug,
        });
    }
    else if (i18nextDefault.language !== lng) {
        await i18nextDefault.changeLanguage(lng);
    }
    return i18nextDefault;
}
/** Re-export the default i18next instance for callers that want raw access. */
export { i18nextDefault as i18n };
//# sourceMappingURL=init.js.map